/**
 * adapters/storage/safeStorage — barreira de higiene de armazenamento (RNF 1, RNF 5, RNF 6.3).
 *
 * Persiste em `localStorage` APENAS preferências não sensíveis, por whitelist
 * (tema, rota, densidade). Qualquer tentativa de gravar uma chave fora da
 * whitelist — em especial token de sessão ou PII — é barrada:
 *   - em desenvolvimento: lança erro (falha ruidosa para pegar o bug cedo);
 *   - em produção: no-op + `console.warn` (nunca quebra a UI, nunca vaza).
 *
 * Tokens vivem apenas em memória + `sessionStorage` (DD-003); PII nunca persiste.
 */

/** Chaves seguras permitidas em `localStorage` (preferências não sensíveis). */
export const SAFE_KEYS = ["azim.theme", "azim.route", "azim.density"] as const;
export type SafeKey = (typeof SAFE_KEYS)[number];

const SAFE_KEY_SET: ReadonlySet<string> = new Set(SAFE_KEYS);

export function isSafeKey(key: string): key is SafeKey {
  return SAFE_KEY_SET.has(key);
}

/** Erro lançado (em dev) ao tentar persistir uma chave não permitida. */
export class UnsafeStorageError extends Error {
  constructor(key: string) {
    super(
      `safeStorage: chave "${key}" não é permitida em localStorage. ` +
        `Apenas preferências não sensíveis (${SAFE_KEYS.join(", ")}) podem ser persistidas. ` +
        `Tokens e PII jamais vão para localStorage (RNF 1/RNF 5).`,
    );
    this.name = "UnsafeStorageError";
  }
}

function rejectUnsafe(key: string): void {
  if (import.meta.env.DEV) {
    throw new UnsafeStorageError(key);
  }
  console.warn(`[safeStorage] gravação bloqueada para chave não permitida: "${key}"`);
}

export const safeStorage = {
  /** Persiste uma preferência segura. Rejeita chaves fora da whitelist. */
  setItem(key: string, value: string): void {
    if (!isSafeKey(key)) {
      rejectUnsafe(key);
      return;
    }
    localStorage.setItem(key, value);
  },

  /** Lê uma preferência segura. Chaves não permitidas retornam `null`. */
  getItem(key: string): string | null {
    if (!isSafeKey(key)) return null;
    return localStorage.getItem(key);
  },

  /** Remove uma preferência segura. */
  removeItem(key: string): void {
    if (!isSafeKey(key)) return;
    localStorage.removeItem(key);
  },
};
